/**
 * carriers/tempest — Tempest carrier (CVN-07)
 * @specialization 전방 구축함 · 외부 통신망(GitHub API) 감청 및 원격 코드 체보 수집 — 외부 GitHub 레포지토리 심층 탐색 · gh API 리서치 · 클론 기반 딝다이브
 *
 * Tempest carrier를 프레임워크에 등록합니다.
 */

import type { CarrierMetadata, CarrierPersonaDefaults } from "../dispatch/types.js";

import { CARRIER_JOBS_SELF_CALL_HINT } from "../constants.js";

export const TEMPEST_DEFAULTS: CarrierPersonaDefaults = {
  id: "tempest",
  displayName: "Tempest",
  slot: 7,
  agent: {
    dispatch: {
      defaultCliType: "claude",
      defaultModel: "sonnet",
      defaultEffort: "medium",
    },
  },
};

export const CARRIER_METADATA: CarrierMetadata = {
  // ── Tier 1: Routing ──
  title: "External Intelligence",
  summary: "GitHub intelligence — external repo investigation via API and clone-based deep analysis of upstream repositories and unfamiliar codebases.",
  category: "operations",
  whenToUse: [
    "external GitHub repo investigation (library internals, upstream dependencies)",
    "API usage examples and patterns from open-source",
    "remote code intelligence gathering for unfamiliar projects",
  ],
  whenNotToUse: [
    "local codebase exploration (→vanguard)",
    "code modification (→genesis)",
  ],
  requestBlocks: [
    { tag: "target_repo", hint: "Repository to investigate (owner/repo format or full URL).", required: true },
    { tag: "objective", hint: "What intelligence is needed — feature, pattern, API usage, or implementation detail.", required: true },
    { tag: "focus_areas", hint: "Specific directories, files, symbols, or code patterns to prioritize.", required: false },
    { tag: "constraints", hint: "Time constraints, specific branches/tags, or areas to exclude.", required: false },
  ],
  allowedExecutorTools: ["carrier_jobs", "plan_read"],
  allowedBuiltinExternalMcpServers: ["grep_app"],

  // ── Tier 2: Composition ──
  permissions: [
    "CRITICAL: When cloning, MUST use OS-native temporary directory (e.g., mktemp -d). NEVER clone into the current working directory or any project path. MUST clean up the cloned directory after analysis.",
    "MUST use grep_app for public code search only; MUST NOT query secrets, internal code, or private repo content.",
    "Full access to gh CLI for GitHub API interactions.",
    "Tempest decides whether API-level exploration suffices or local clone is needed for deeper analysis.",
    "If the request fails (timeout/rate limit), retry up to 3 times before reporting failure.",
  ],
  principles: [CARRIER_JOBS_SELF_CALL_HINT],
  outputFormat:
    `Report findings as a structured intelligence briefing.\n` +
    `[Required] always include:\n` +
    `  **Intelligence findings** — Organized list of discoveries relevant to the objective:\n` +
    `    - Code patterns, implementation details, or API usage examples found.\n` +
    `    - Include relevant code snippets (keep each under 20 lines).\n` +
    `  **Confidence level** — high / medium / low — based on depth of investigation achieved.\n` +
    `[If applicable] omit if not relevant:\n` +
    `  **Repository overview** — 1-2 sentences on what the repo is and its relevance.\n` +
    `  **Key code paths** — Important files and directories with brief descriptions. Use owner/repo relative paths.\n` +
    `Keep the report concise — bullets and short lines only. No narrative paragraphs. Never prescribe how findings should be applied to our codebase or suggest follow-up actions; application and routing decisions belong to the orchestrator.`,
};
